import type { PartnerBoxMapContent } from '$lib/components/advertising/partners/types';
import type { PageLoad } from './$types';
import { browser } from '$app/environment';
import { AdvertisingPartnerType, GetPartnersBoxMapsStore } from '$houdini';
import { getSelectedAddress } from '$lib/components/map/selectedAddress.svelte';
import { POI_LIST_PAGE_DATASHEET_HEIGHT_OFFSET } from '$lib/constants/drawer';
import { isHomePage } from '$lib/services/urls/routeIds';
import { routesPagename } from '$lib/services/urls/routesPagename';
import { augmentHoudiniStore } from '$lib/utils/augmentedHoudiniStore';
import { getBookingUrl } from '$lib/utils/getBookingUrl';
import { sessionPoiListCanonicalLink } from '$lib/utils/sessionStorage';
import { error } from '@sveltejs/kit';
import { derived, get, readable } from 'svelte/store';
import { enrichPoisStoreWithSelectedPoiData, getPoisStore } from '../../../loadPois';
import { getStaticMap } from './getStaticMap';
import { getPageSeoData } from './metas';

export const load = (async (event) => {
	const canonicalLinkOfCanonicalPage = event.params.address;
	const { poisStore: cachedPoisStore, lang, locale, searchParams, searchPOIStore, searchPOIFromAddressStore } = await event.parent();
	const { page, selectedPoi } = searchParams;

	if (page <= 0) {
		return error(404);
	}

	const poisStoreWithoutSelectedPoi = await getPoisStore({
		urlAddress: canonicalLinkOfCanonicalPage,
		event,
		page,
		searchParams: { ...searchParams, poiCategories: isHomePage(event.url) && !searchParams.poiCategories ? [] : searchParams.poiCategories },
		searchPOIStore,
		searchPOIFromAddressStore,
		cachedPoisStore,
	});

	const poisStore = await enrichPoisStoreWithSelectedPoiData(
		poisStoreWithoutSelectedPoi,
		selectedPoi,
		event,
	);

	const staticMapStore = !event.params.category
		? await getStaticMap(event, get(poisStore), canonicalLinkOfCanonicalPage)
		: readable(undefined);

	const seoDataStore = derived([staticMapStore, poisStore], ([$staticMapStore, $poisStore]) => {
		return getPageSeoData(event, {
			searchParams,
			locale,
			poisStore: $poisStore,
			staticMapStore: $staticMapStore,
			lang,
			poiList: get(poisStoreWithoutSelectedPoi)?.poisList ?? [],
		});
	});

	const isMapResultPageFromSearchParams = searchParams?.selectedAddress?.address && searchParams.selectedAddress.entityType;

	if (browser && sessionPoiListCanonicalLink) {
		if (searchParams.filters?.ACCOMMODATION_AVAILABILITY_DATE_RANGE) {
			sessionPoiListCanonicalLink.remove?.();
		} else {
			const pageCanonicalLink = get(seoDataStore).canonicalLink;
			if (pageCanonicalLink) {
				sessionPoiListCanonicalLink.set?.(pageCanonicalLink);
			} else {
				sessionPoiListCanonicalLink.remove?.();
			}
		}
	}

	const isMapResultPage = event.params.address || isMapResultPageFromSearchParams;
	const mobileTopButtons = ['search'];

	if (isHomePage(event.url)) {
		mobileTopButtons.push('pois-hrt');
	}

	const getPartnersBoxMapsStore = augmentHoudiniStore(new GetPartnersBoxMapsStore());

	const $poisStore = get(poisStore);
	const selectedAddress = getSelectedAddress();
	const coordinates = selectedAddress?.mapLocation?.location ?? $poisStore?.selectedAddress?.coordinates;
	const getPartnersBoxMapsInput = ($poisStore?.selectedAddress || selectedAddress?.mapLocation?.location)
		? {
				departureName: undefined,
				arrivalName: (selectedAddress?.address?.city || $poisStore?.selectedAddress?.address) || '',
				coordinates: coordinates ? [coordinates] : [],
			}
		: null;

	if (getPartnersBoxMapsInput) {
		getPartnersBoxMapsStore.fetch({ event, policy: 'NetworkOnly', variables: { input: getPartnersBoxMapsInput } }).catch(() => {
			console.error('Error fetching partners box maps');
		});
	}

	const boxmapsForPageStore = derived([getPartnersBoxMapsStore], ([$getPartnersBoxMapsStore]) => {
		const partnersBoxMaps = $getPartnersBoxMapsStore.data?.getPartnersBoxMaps;

		if (!partnersBoxMaps || partnersBoxMaps.__typename !== 'GetPartnersBoxMapsResultSuccess') {
			return [];
		}

		return partnersBoxMaps.boxMaps?.filter(
			(item): item is PartnerBoxMapContent => item !== null && item !== undefined && !('errors' in item),
		).map(item => item.type === AdvertisingPartnerType.BOOKING
			? {
					...item,
					url: getBookingUrl({
						bookUrl: item.url,
						contentType: 'boxmap',
						arrival: getPartnersBoxMapsInput?.arrivalName ?? '',
						departure: getPartnersBoxMapsInput?.departureName ?? '',
						distance: '',
						locale,
					}),
				}
			: item);
	});

	return {
		page,
		poisStore,
		mobileTopButtons,
		bottomsheetHeightOffset: POI_LIST_PAGE_DATASHEET_HEIGHT_OFFSET,
		pagename: isMapResultPage ? routesPagename.MAP_RESULT : routesPagename.HOME_PAGE,
		seoDataStore,
		boxmapsForPageStore,
	};
}) satisfies PageLoad;
